import { useCallback, useEffect, useMemo, useState } from "react";
import { Copy, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { AppShell } from "@/components/AppShell";
import { adminService } from "@/services/adminService";
import { errorMessage } from "@/services/apiClient";
import { fmtDate, fmtMoney } from "@/lib/format";
import { brandService } from "@/services/brandService";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ADMIN_NAV } from "./nav";

const field = "h-11 rounded-md border-[#22252F] bg-[#0F1116] text-white focus-visible:ring-[#2563EB]";
const labelCls = "text-xs uppercase tracking-[0.2em] text-[#94A3B8]";

export default function AdminInviteCodesPage() {
  const [codes, setCodes] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ code: "", label: "", max_uses: 1, initial_balance: 250, brand_id: "" });
  const [brands, setBrands] = useState([]);
  const activeBrands = useMemo(() => brands.filter((b) => b.active), [brands]);

  const load = useCallback(async () => {
    try {
      setCodes(await adminService.listCodes());
      setBrands(await brandService.list());
    } catch (e) {
      toast.error(errorMessage(e));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const create = async (e) => {
    e.preventDefault();
    try {
      await adminService.createCode({
        code: form.code ? form.code.trim().toUpperCase() : null,
        label: form.label || null,
        max_uses: Number(form.max_uses) || 1,
        initial_balance: Number(form.initial_balance) || 0,
        brand_id: form.brand_id || null,
      });
      toast.success("Código criado");
      setOpen(false);
      setForm({ code: "", label: "", max_uses: 1, initial_balance: 250, brand_id: "" });
      load();
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  const toggle = async (c) => {
    try {
      await adminService.updateCode(c.id, { active: !c.active });
      load();
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Remover o código ${c.code}?`)) return;
    try {
      await adminService.deleteCode(c.id);
      toast.success("Código removido");
      load();
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  return (
    <AppShell nav={ADMIN_NAV} title="Códigos de registo" subtitle="Só é possível criar conta com um código válido">
      <div data-testid="admin-codes-page" className="space-y-6">
        <div className="flex justify-end">
          <Button data-testid="create-code-button" onClick={() => setOpen(true)}
                  className="h-11 rounded-md bg-[#2563EB] text-xs font-semibold uppercase tracking-[0.15em] transition-colors hover:bg-[#1D4ED8]">
            <Plus className="mr-2 h-4 w-4" /> Gerar código
          </Button>
        </div>

        <div className="overflow-x-auto rounded-md border border-[#22252F] bg-[#12141A]">
          <table className="w-full min-w-[760px] text-left text-sm">
            <thead className="border-b border-[#22252F] text-[11px] uppercase tracking-[0.2em] text-[#94A3B8]">
              <tr>
                <th className="px-6 py-4">Código</th>
                <th className="px-6 py-4">Etiqueta</th>
                <th className="px-6 py-4 text-right">Usos</th>
                <th className="px-6 py-4 text-right">Saldo inicial</th>
                <th className="px-6 py-4">Brand</th>
                <th className="px-6 py-4">Estado</th>
                <th className="px-6 py-4">Criado</th>
                <th className="px-6 py-4 text-right">Ações</th>
              </tr>
            </thead>
            <tbody data-testid="codes-table-body">
              {codes.map((c) => (
                <tr key={c.id} data-testid={`code-row-${c.code}`} className="border-b border-[#1A1D25] transition-colors hover:bg-[#151821]">
                  <td className="px-6 py-4 font-mono tracking-[0.2em] text-white">{c.code}</td>
                  <td className="px-6 py-4 text-[#94A3B8]">{c.label || "—"}</td>
                  <td className="px-6 py-4 text-right font-mono text-[#94A3B8]">{c.uses}/{c.max_uses}</td>
                  <td className="px-6 py-4 text-right font-mono text-white">{fmtMoney(c.initial_balance)}</td>
                  <td className="px-6 py-4 font-mono text-xs uppercase tracking-[0.2em] text-[#60A5FA]"
                      data-testid={`code-brand-${c.code}`}>
                    {brands.find((b) => b.id === c.brand_id)?.name || "Meridian"}
                  </td>
                  <td className="px-6 py-4">
                    <span className={`font-mono text-xs uppercase ${c.active ? "text-[#10B981]" : "text-[#EF4444]"}`}>
                      {c.active ? "ativo" : "inativo"}
                    </span>
                  </td>
                  <td className="px-6 py-4 font-mono text-xs text-[#64748B]">{fmtDate(c.created_at)}</td>
                  <td className="px-6 py-4">
                    <div className="flex justify-end gap-2">
                      <button data-testid={`copy-code-${c.code}`}
                              onClick={() => { navigator.clipboard?.writeText(c.code); toast.success("Código copiado"); }}
                              className="rounded-md border border-[#22252F] p-2 text-[#94A3B8] transition-colors hover:border-[#2563EB]/50 hover:text-white">
                        <Copy className="h-4 w-4" />
                      </button>
                      <button data-testid={`toggle-code-${c.code}`} onClick={() => toggle(c)}
                              className="rounded-md border border-[#22252F] px-3 py-2 text-xs text-[#94A3B8] transition-colors hover:border-[#2563EB]/50 hover:text-white">
                        {c.active ? "Desativar" : "Ativar"}
                      </button>
                      <button data-testid={`delete-code-${c.code}`} onClick={() => remove(c)}
                              className="rounded-md border border-[#22252F] p-2 text-[#94A3B8] transition-colors hover:border-[#EF4444]/50 hover:text-[#EF4444]">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {codes.length === 0 ? (
                <tr><td colSpan={7} className="px-6 py-10 text-center text-sm text-[#64748B]">Ainda não existem códigos</td></tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="border-[#22252F] bg-[#12141A] text-white" data-testid="create-code-dialog">
          <DialogHeader><DialogTitle className="font-heading font-light tracking-tight">Gerar código de registo</DialogTitle></DialogHeader>
          <form onSubmit={create} className="space-y-4">
            <div className="space-y-2"><Label className={labelCls}>Código (vazio = automático)</Label>
              <Input data-testid="new-code-value" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })}
                     placeholder="EX: MERIDIAN01" className={`${field} font-mono uppercase tracking-[0.2em]`} /></div>
            <div className="space-y-2"><Label className={labelCls}>Etiqueta</Label>
              <Input data-testid="new-code-label" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} className={field} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label className={labelCls}>Máx. usos</Label>
                <Input type="number" min="1" data-testid="new-code-max-uses" value={form.max_uses}
                       onChange={(e) => setForm({ ...form, max_uses: e.target.value })} className={`${field} font-mono`} /></div>
              <div className="space-y-2"><Label className={labelCls}>Saldo inicial</Label>
                <Input type="number" step="0.01" data-testid="new-code-balance" value={form.initial_balance}
                       onChange={(e) => setForm({ ...form, initial_balance: e.target.value })} className={`${field} font-mono`} /></div>
            </div>
            <div className="space-y-2">
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#94A3B8]">Brand do registo</p>
              <select data-testid="new-code-brand" value={form.brand_id}
                      onChange={(e) => setForm({ ...form, brand_id: e.target.value })}
                      className="h-11 w-full rounded-md border border-[#22252F] bg-[#0F1116] px-3 text-sm text-white">
                <option value="">Sem Brand (Meridian)</option>
                {activeBrands.map((b) => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
            </div>
            <DialogFooter>
              <Button type="submit" data-testid="submit-new-code" className="rounded-md bg-[#2563EB] hover:bg-[#1D4ED8]">Gerar</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
